<template>
  <div>
    <div class="content-header">
      <div class="content-header-left col-md-9 col-12 mb-2">
        <div class="row breadcrumbs-top">
          <div class="col-12">
            <h2 class="content-header-title float-left mb-0">
              Task Log
            </h2>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Component -->
    <TaskLogFilters
      :manual-advertiser-id="manualAdvertiserId"
      :filters="filters"
      :saving-default="savingDefault"
      @manual-advertiser-input="onManualAdvertiserInput"
      @save-default-advertiser="saveAsDefaultAdvertiser"
      @use-manual-advertiser="useManualAdvertiser"
      @filter-change="onFilterChange"
      @clear-filters="clearFilters"
      @debug-advertiser-state="debugAdvertiserState"
    />

    <!-- Table Component -->
    <TaskLogTable
      :tasks="tasks"
      :loading="loading"
      :total-tasks="totalTasks"
      :current-page="currentPage"
      :page-size="pageSize"
      :preview-advertiser-id="previewAdvertiserId"
      :highlight-task-id="highlightTaskId"
      @show-task-details="showTaskDetails"
      @show-creative-preview="showCreativePreview"
      @refresh-task-status="refreshTaskStatus"
      @page-change="onPageChange"
    />

    <!-- Task Details Modal Component -->
    <TaskDetailsModal
      :show="showTaskDetailsModal"
      :selected-task="selectedTask"
      @input="showTaskDetailsModal = $event"
      @show-creative-preview="showCreativePreview"
    />

    <!-- Creative Preview Modal Component -->
    <CreativePreviewModal
      :show="showPreviewModal"
      :preview-item="previewItem"
      @input="showPreviewModal = $event"
    />
  </div>
</template>

<script>
import { toastification } from '@core/mixins/toast'
import CreativeService from '@/services/CreativeService'

// Import new components
import TaskLogFilters from './components/TaskLogFilters.vue'
import TaskLogTable from './components/TaskLogTable.vue'
import TaskDetailsModal from './components/TaskDetailsModal.vue'
import CreativePreviewModal from './components/CreativePreviewModal.vue'

export default {
  name: 'PrePreviewTaskLog',
  components: {
    TaskLogFilters,
    TaskLogTable,
    TaskDetailsModal,
    CreativePreviewModal,
  },
  mixins: [toastification],
  data() {
    return {
      loading: false,
      tasks: [],
      totalTasks: 0,
      currentPage: 1,
      pageSize: 10,
      filters: {
        status: '',
        tiktokTaskId: '',
      },
      showTaskDetailsModal: false,
      selectedTask: null,
      showPreviewModal: false,
      previewItem: null,
      selectedAdvertiserId: null,
      manualAdvertiserId: '',
      savingDefault: false,
      highlightTaskId: null,
    }
  },
  computed: {
    availableAdvertiserAccounts() {
      // Return empty array for now - will be populated by API call
      return []
    },

    previewAdvertiserId() {
      // First priority: manually entered advertiser ID
      if (this.manualAdvertiserId) {
        return this.manualAdvertiserId
      }

      // Second priority: manually selected advertiser from UI
      if (this.selectedAdvertiserId) {
        return this.selectedAdvertiserId
      }

      // Third priority: saved default from localStorage
      const defaultAdvertiserId = localStorage.getItem('defaultPreviewAdvertiserId')
      if (defaultAdvertiserId) {
        return defaultAdvertiserId
      }

      // Fourth priority: currently selected advertiser from store (disabled for now)
      // if (this.currentAdvertiserId) {
      //   return this.currentAdvertiserId
      // }

      // Fifth priority: first available account (auto-select)
      const accounts = this.availableAdvertiserAccounts
      if (accounts.length > 0) {
        return accounts[0].id
      }

      // Sixth priority: try to get from localStorage userData
      const advertiserId = this.getAdvertiserIdFromLocalStorage()
      if (advertiserId) {
        return advertiserId
      }

      return null
    },
  },
  async mounted() {
    // Check if there's a taskId in the query params to highlight/filter
    if (this.$route.query.taskId) {
      this.highlightTaskId = this.$route.query.taskId
    }

    // Check if there's an advertiserId in the query params
    if (this.$route.query.advertiserId) {
      this.manualAdvertiserId = this.$route.query.advertiserId
      this.selectedAdvertiserId = this.$route.query.advertiserId
    }

    // Try to get saved default advertiser
    const savedDefault = this.getAdvertiserIdFromLocalStorage()
    if (savedDefault) {
      this.selectedAdvertiserId = savedDefault
      // Also set manualAdvertiserId to show the saved default in the input field
      if (!this.manualAdvertiserId) {
        this.manualAdvertiserId = savedDefault
      }
    }

    // Access token handling removed - now handled by backend

    await this.fetchTasks()
  },
  methods: {

    // Get advertiser ID from localStorage userData as fallback
    getAdvertiserIdFromLocalStorage() {
      try {
        // First, check for saved default pre-preview advertiser
        const defaultAdvertiserId = localStorage.getItem('defaultPreviewAdvertiserId')
        if (defaultAdvertiserId) {
          return defaultAdvertiserId
        }

        // Then check userData
        const userData = localStorage.getItem('userData')
        if (userData) {
          const parsedData = JSON.parse(userData)

          // Check for saved default in userData first
          if (parsedData?.defaultPreviewAdvertiserId) {
            return parsedData.defaultPreviewAdvertiserId
          }

          // Check various possible paths for advertiser ID
          const advertiserId = parsedData?.tiktok?.id
            || parsedData?.advertiser_id
            || parsedData?.id
            || parsedData?.tiktokAccount?.id

          if (advertiserId) {
            return advertiserId
          }
        }
      } catch (error) {
        // Error handling removed for production
      }
      return null
    },

    async fetchTasks() {
      this.loading = true
      try {
        // Ensure previewAdvertiserId is available
        if (!this.previewAdvertiserId) {
          this.toastFailure('Please enter an advertiser ID in the input field above and click the check button to load tasks.')
          this.loading = false
          return
        }

        const params = {
          advertiser_id: this.previewAdvertiserId,
          page: this.currentPage,
          size: this.pageSize,
        }

        if (this.filters.status) {
          params.status = this.filters.status
        }

        if (this.filters.tiktokTaskId) {
          params.tiktok_task_id = this.filters.tiktokTaskId
        }

        const response = await CreativeService.getTaskHistory(params)

        // Handle the response structure from the backend
        if (response.data) {
          let tasksData = null
          let totalCount = 0

          // Check if it's the new API format with result wrapper
          if (response.data.result) {
            // Check if result contains tasks array or is the tasks array itself
            if (Array.isArray(response.data.result)) {
              // result IS the tasks array
              tasksData = response.data.result
              totalCount = response.data.result.length
            } else if (response.data.result.tasks) {
              // result contains tasks property
              tasksData = response.data.result.tasks
              totalCount = response.data.result.total || response.data.result.tasks.length || 0
            } else {
              // result might be a single task object, wrap it in array
              tasksData = [response.data.result]
              totalCount = 1
            }
          } else if (response.data.tasks) {
            // Legacy format
            tasksData = response.data.tasks
            totalCount = response.data.total || 0
          } else if (Array.isArray(response.data)) {
            // Direct array response
            tasksData = response.data
            totalCount = response.data.length
          }

          if (Array.isArray(tasksData)) {
            this.tasks = tasksData.map(task => this.mapTaskData(task))
            this.totalTasks = totalCount

            // If we have a highlight task ID, find and auto-show its details
            if (this.highlightTaskId) {
              const highlightedTask = this.tasks.find(task => task.taskId === this.highlightTaskId)
              if (highlightedTask) {
                // Auto-show the task details modal
                setTimeout(() => {
                  this.showTaskDetails(highlightedTask)
                }, 500)
              } else {
                this.toastInfo(`Task ${this.highlightTaskId} might be on a different page or not yet loaded.`)
              }
            }
          } else {
            // No tasks found
            this.tasks = []
            this.totalTasks = 0
          }
        } else {
          this.tasks = []
          this.totalTasks = 0
        }
      } catch (error) {
        // Show real API error instead of fallback to mock data
        let errorMessage = 'Failed to load task history'

        if (error.status === 401) {
          errorMessage = 'Unauthorized - Please check your login status'
        } else if (error.status === 400) {
          errorMessage = error.data?.message || 'Bad request - Check advertiser ID'
        } else if (error.status === 403) {
          errorMessage = 'Forbidden - You may not have access to this advertiser account'
        } else if (error.status === 404) {
          errorMessage = 'API endpoint not found'
        } else if (error.status >= 500) {
          errorMessage = 'Server error - Please try again later'
        }

        this.toastFailure(errorMessage)

        // Clear tasks array on error
        this.tasks = []
        this.totalTasks = 0
      } finally {
        this.loading = false
      }
    },

    mapTaskData(backendTask) {
      // Map backend task structure to frontend expected structure

      // Check if backend task has tiktok_response with pre_review_result_list
      if (backendTask.tiktok_response && backendTask.tiktok_response.pre_review_result_list) {
        return this.mapTaskDetailsData(backendTask.tiktok_response, {
          taskId: backendTask.task_id,
          tiktokRequestId: backendTask.tiktok_request_id,
          createdAt: backendTask.created_at,
          completedAt: backendTask.completed_at,
          submittedAt: backendTask.submitted_at,
          materials: backendTask.materials, // Pass original materials
        })
      }

      // Check if backend task has pre_review_result_list (TikTok format embedded in BE response)
      if (backendTask.pre_review_result_list) {
        return this.mapTaskDetailsData(backendTask, {
          taskId: backendTask.task_id,
          tiktokRequestId: backendTask.tiktok_request_id,
          createdAt: backendTask.created_at,
          completedAt: backendTask.completed_at,
          submittedAt: backendTask.submitted_at,
          materials: backendTask.materials, // Pass original materials
        })
      }

      // Handle the BE API format with materials and results arrays
      const materials = (backendTask.materials || []).map((material, index) => {
        // Get corresponding result - note: results may have different count than materials
        // If there are more results than materials, use the last result for all materials
        let result = {}
        if (backendTask.results && backendTask.results.length > 0) {
          if (backendTask.results[index]) {
            result = backendTask.results[index]
          } else {
            // Use the last available result if index exceeds results array
            // This handles case where 1 material has multiple results
            result = backendTask.results[backendTask.results.length - 1]
          }
        }

        // Check if result has pre_review_status directly
        let preReviewStatus = result.pre_review_status || result.status

        // If no explicit status, try to infer from task status and result data
        if (!preReviewStatus) {
          // If task is COMPLETED and result has reviewed_at, likely approved
          if (backendTask.status === 'COMPLETED' && result.reviewed_at) {
            // If there are no suggestions/reject reasons, assume approved
            if (!result.suggestions || result.suggestions.length === 0) {
              preReviewStatus = 'APPROVED'
            } else {
              preReviewStatus = 'REJECTED'
            }
          } else if (result.reason) {
            // Fallback to reason-based determination
            if (result.reason.toLowerCase().includes('meets')
                || result.reason.toLowerCase().includes('guidelines')
                || result.reason.toLowerCase().includes('approved')
                || (result.metadata && result.metadata.confidence_score > 0.8)) {
              preReviewStatus = 'APPROVED'
            } else if (result.reason.toLowerCase().includes('reject')
                || result.reason.toLowerCase().includes('violat')
                || result.reason.toLowerCase().includes('prohibit')) {
              preReviewStatus = 'REJECTED'
            } else {
              preReviewStatus = 'UNAVAILABLE'
            }
          } else {
            preReviewStatus = 'UNAVAILABLE'
          }
        }

        const mappedMaterial = {
          ...material,
          // Ensure metadata is preserved (including nested structures)
          metadata: material.metadata || {},
          // Extract creative_id from metadata first, then fallback to material.creative_id
          creative_id: material.id || material.metadata?.creative_id
            || material.metadata?.tiktok_material_id
            || material.creative_id,
          // Preserve content (URL) separately
          content: material.content,
          // Preserve filename if available
          filename: material.filename || material.original_filename || material.metadata?.filename,
          original_filename: material.original_filename || material.metadata?.original_filename,
          material_type: material.type || material.material_type,
          // Add pre-review status from results analysis
          pre_review_status: preReviewStatus,
          status: preReviewStatus,
          reject_info_list: result.reject_info_list || result.suggestions || [],
          location_code: backendTask.location || backendTask.location_code,
          is_ecommerce: backendTask.e_commerce || backendTask.is_ecommerce || false,
          reviewed_at: result.reviewed_at || result.result_creation_time,
          confidence_score: result.metadata?.confidence_score || result.confidence_score,
          reason: result.reason,
          result_expiration_time: result.result_expiration_time,
        }

        return mappedMaterial
      })

      // Enhanced results mapping for display
      const enhancedResults = materials.map(material => ({
        creative_id: material.creative_id,
        content: material.content, // Include the actual content for display
        type: material.type || material.material_type,
        material_type: material.material_type || material.type,
        filename: material.filename,
        original_filename: material.original_filename,
        pre_review_status: material.pre_review_status,
        status: material.status,
        reject_info_list: material.reject_info_list,
        reason: material.reason,
        reviewed_at: material.reviewed_at,
        confidence_score: material.confidence_score,
      }))

      return {
        taskId: backendTask.task_id,
        tiktokRequestId: backendTask.tiktok_request_id || backendTask.tiktok_task_id || null,
        status: this.mapTaskStatus(backendTask.status),
        materialsCount: materials.length,
        createdAt: backendTask.created_at,
        completedAt: backendTask.completed_at,
        submittedAt: backendTask.submitted_at,
        materials,
        results: enhancedResults,
        advertiser_id: backendTask.advertiser_id,
      }
    },

    mapTaskStatus(backendStatus) {
      // Map TikTok API task status to frontend display status
      const statusMap = {
        PROCESSING: 'processing',
        COMPLETED: 'completed',
        SUCCESS: 'completed',
        FAILED: 'failed',
        // Additional mappings
        IN_PROGRESS: 'processing',
        PENDING: 'pending',
      }

      // Safety check for null, undefined, or non-string values
      if (!backendStatus || typeof backendStatus !== 'string') {
        return 'unknown'
      }

      const mapped = statusMap[backendStatus.toUpperCase()] || backendStatus.toLowerCase()
      // console.log(`Mapping status: ${backendStatus} -> ${mapped}`) // Debug log
      return mapped
    },

    async showTaskDetails(task) {
      try {
        // First try to sync with TikTok to get the latest data with real material IDs
        const syncResponse = await CreativeService.syncTaskStatus(task.taskId, this.previewAdvertiserId)

        // console.log('Sync response for modal:', syncResponse.data) // Debug log

        // Check the sync response format and map accordingly
        if (syncResponse.data && syncResponse.data.result) {
          const { result } = syncResponse.data

          // console.log('Result from sync:', result) // Debug log
          // console.log('Result materials:', result.materials) // Debug log
          // console.log('Result results:', result.results) // Debug log

          // Check if the sync brought back TikTok response data with real material IDs
          if (result.tiktok_response && result.tiktok_response.pre_review_result_list) {
            // console.log('Using tiktok_response data:', result.tiktok_response) // Debug log
            this.selectedTask = this.mapTaskDetailsData(result.tiktok_response, {
              ...task,
              taskId: result.task_id || task.taskId,
              tiktokRequestId: result.tiktok_request_id || task.tiktokRequestId,
              createdAt: result.created_at || task.createdAt,
              completedAt: result.completed_at || task.completedAt,
              submittedAt: result.submitted_at || task.submittedAt,
            })
          } else if (result.pre_review_result_list) {
            // Result directly contains pre_review_result_list
            // console.log('Using direct pre_review_result_list:', result.pre_review_result_list) // Debug log
            this.selectedTask = this.mapTaskDetailsData(result, task)
          } else if (result.results && result.results.length > 0 && result.results[0].material_id) {
            // Results array contains TikTok material IDs
            // console.log('Using results array with material_id:', result.results) // Debug log
            // Transform results array to pre_review_result_list format
            const transformedData = {
              pre_review_result_list: result.results.map(r => ({
                material_id: r.material_id || r.creative_id,
                material_type: r.material_type || r.type || 'IMAGE',
                pre_review_status: r.pre_review_status || r.status || 'UNAVAILABLE',
                reject_info_list: r.reject_info_list || r.suggestions || [],
                location_code: r.location_code || 'US',
                is_ecommerce: r.is_ecommerce || false,
              })),
              task_status: result.status || 'COMPLETED',
            }
            this.selectedTask = this.mapTaskDetailsData(transformedData, {
              ...task,
              taskId: result.task_id || task.taskId,
              tiktokRequestId: result.tiktok_request_id || task.tiktokRequestId,
              createdAt: result.created_at || task.createdAt,
              completedAt: result.completed_at || task.completedAt,
            })
          } else {
            // Fallback to regular task details API
            // console.log('No TikTok data in sync, falling back to details API') // Debug log
            await this.fetchTaskDetailsWithoutSync(task)
          }
        } else if (syncResponse.data && syncResponse.data.data && syncResponse.data.data.pre_review_result_list) {
          // Direct TikTok API response format
          // console.log('Using direct TikTok API format:', syncResponse.data.data) // Debug log
          this.selectedTask = this.mapTaskDetailsData(syncResponse.data.data, task)
        } else {
          // Fallback to regular task details API
          // console.log('Sync response format not recognized, falling back') // Debug log
          await this.fetchTaskDetailsWithoutSync(task)
        }

        // console.log('Final selected task:', this.selectedTask) // Debug log
      } catch (error) {
        // console.error('Sync failed:', error) // Debug log
        // If sync fails, try regular task details API
        try {
          await this.fetchTaskDetailsWithoutSync(task)
        } catch (detailsError) {
          // Use the task data we already have as fallback
          this.selectedTask = task
        }
      }
      this.showTaskDetailsModal = true
    },

    async fetchTaskDetailsWithoutSync(task) {
      const response = await CreativeService.getTaskDetails(task.taskId, this.previewAdvertiserId)

      // Check if it's a TikTok API response with data.pre_review_result_list
      if (response.data && response.data.data && response.data.data.pre_review_result_list) {
        // Direct TikTok API response format
        this.selectedTask = this.mapTaskDetailsData(response.data.data, task)
      } else if (response.data && response.data.result) {
        // Our backend API response with result wrapper
        const { result } = response.data

        // Check if result has tiktok_response with pre_review_result_list
        if (result.tiktok_response && result.tiktok_response.pre_review_result_list) {
          this.selectedTask = this.mapTaskDetailsData(result.tiktok_response, task)
        } else if (result.pre_review_result_list) {
          // Result directly contains pre_review_result_list
          this.selectedTask = this.mapTaskDetailsData(result, task)
        } else {
          // Regular BE format - preserve the existing task data
          const mappedTask = this.mapTaskData(result)
          // If the mapped task has no materials but the original task does, use original
          if ((!mappedTask.materials || mappedTask.materials.length === 0) && task.materials && task.materials.length > 0) {
            mappedTask.materials = task.materials
            mappedTask.results = task.results || []
          }
          this.selectedTask = mappedTask
        }
      } else if (response.data && response.data.pre_review_result_list) {
        // Direct response with pre_review_result_list
        this.selectedTask = this.mapTaskDetailsData(response.data, task)
      } else {
        // Try mapping as regular task data
        this.selectedTask = this.mapTaskData(response.data)
      }
    },

    mapTaskDetailsData(tiktokApiData, originalTask) {
      // Map TikTok API response structure to frontend expected structure
      const preReviewList = tiktokApiData.pre_review_result_list || []

      // Get the original materials which contain the actual content
      const originalMaterials = originalTask.materials || []

      // If we have pre_review_result_list from TikTok, use it
      let mappedMaterials = []
      let results = []

      if (preReviewList.length > 0) {
        // Map materials from TikTok response with proper structure
        mappedMaterials = preReviewList.map((material, index) => {
          // Try to find matching original material by index or type
          const originalMaterial = originalMaterials[index]
            || originalMaterials.find(m => m.type === material.material_type || m.material_type === material.material_type)

          // Use content from original material if available
          const actualContent = originalMaterial?.content
            || material.content || material.text || material.ad_text || material.material_id

          return {
            creative_id: material.material_id, // Use the actual material_id from TikTok
            content: actualContent, // Use actual content from original or response
            // Preserve filename from original task if available
            filename: originalMaterial?.filename || originalTask.materials?.[index]?.filename || material.filename,
            original_filename: originalMaterial?.original_filename
              || originalTask.materials?.[index]?.original_filename || material.original_filename,
            material_type: material.material_type,
            type: material.material_type,
            pre_review_status: material.pre_review_status || 'UNAVAILABLE',
            status: material.pre_review_status || 'UNAVAILABLE',
            location_code: material.location_code,
            is_ecommerce: material.is_ecommerce,
            reject_info_list: material.reject_info_list || [],
            result_creation_time: material.result_creation_time,
            result_expiration_time: material.result_expiration_time,
          }
        })

        // Create results array for the results table
        results = mappedMaterials.map(material => ({
          creative_id: material.creative_id,
          content: material.content, // Include the actual content
          material_type: material.material_type, // Include material type
          type: material.type, // Include type
          filename: material.filename,
          original_filename: material.original_filename,
          pre_review_status: material.pre_review_status,
          status: material.status,
          reject_info_list: material.reject_info_list,
        }))
      } else if (originalTask.materials && originalTask.materials.length > 0) {
        // Fallback to original task materials if no TikTok response
        mappedMaterials = originalTask.materials
        results = originalTask.results || originalTask.materials.map(m => ({
          creative_id: m.creative_id,
          content: m.content, // Include the actual content
          material_type: m.material_type || m.type, // Include material type
          type: m.type || m.material_type, // Include type
          filename: m.filename,
          original_filename: m.original_filename,
          pre_review_status: m.pre_review_status || m.status || 'UNAVAILABLE',
          status: m.status || 'UNAVAILABLE',
          reject_info_list: m.reject_info_list || [],
        }))
      }

      return {
        taskId: originalTask.taskId,
        tiktokRequestId: tiktokApiData.tiktok_request_id || tiktokApiData.tiktok_task_id || originalTask.tiktokRequestId,
        status: this.mapTaskStatus(tiktokApiData.task_status || 'SUCCESS'),
        materialsCount: mappedMaterials.length,
        createdAt: originalTask.createdAt,
        completedAt: originalTask.completedAt,
        submittedAt: originalTask.submittedAt,
        materials: mappedMaterials,
        results,
        advertiser_id: this.previewAdvertiserId,
      }
    },

    showCreativePreview(material, task) {
      // console.log('Creative preview material:', material) // Debug log
      // console.log('Creative ID being shown:', material.creative_id) // Debug log
      this.previewItem = {
        ...material,
        task_id: task.taskId,
        // Ensure we use the correct creative_id from material
        creative_id: material.creative_id || material.material_id,
      }
      this.showPreviewModal = true
    },

    async refreshTaskStatus(task) {
      try {
        // Call the sync endpoint to fetch latest status from TikTok
        const response = await CreativeService.syncTaskStatus(task.taskId, this.previewAdvertiserId)

        // console.log('Sync response:', response.data) // Debug log

        // Check the response format and map accordingly
        let updatedTask
        if (response.data && response.data.result) {
          // BE API response with result wrapper
          const { result } = response.data

          // Check if the sync brought back TikTok response data
          if (result.tiktok_response && result.tiktok_response.pre_review_result_list) {
            // Use TikTok response data and merge with task data
            updatedTask = this.mapTaskDetailsData(result.tiktok_response, {
              taskId: result.task_id || task.taskId,
              tiktokRequestId: result.tiktok_request_id || task.tiktokRequestId,
              createdAt: result.created_at || task.createdAt,
              completedAt: result.completed_at || task.completedAt,
              submittedAt: result.submitted_at || task.submittedAt,
            })
            // Override status if TikTok says SUCCESS/COMPLETED
            if (result.tiktok_response.task_status === 'SUCCESS' || result.tiktok_response.task_status === 'COMPLETED') {
              updatedTask.status = 'completed'
            }
          } else if (result.pre_review_result_list) {
            // Result itself contains TikTok format
            updatedTask = this.mapTaskDetailsData(result, task)
          } else {
            // Result contains BE format
            updatedTask = this.mapTaskData(result)
          }
        } else if (response.data && response.data.data && response.data.data.pre_review_result_list) {
          // Direct TikTok API response format
          updatedTask = this.mapTaskDetailsData(response.data.data, task)
        } else if (response.data && response.data.pre_review_result_list) {
          // Direct response with pre_review_result_list
          updatedTask = this.mapTaskDetailsData(response.data, task)
        } else {
          // Regular task data format
          updatedTask = this.mapTaskData(response.data)
        }

        const index = this.tasks.findIndex(t => t.taskId === task.taskId)
        if (index !== -1) {
          this.tasks.splice(index, 1, { ...this.tasks[index], ...updatedTask })
        }

        this.toastSuccess('Task synced with TikTok successfully')
      } catch (error) {
        // console.error('Sync error:', error)
        this.toastFailure('Failed to sync task status with TikTok')
      }
    },

    onManualAdvertiserInput(value) {
      this.manualAdvertiserId = value.trim()
    },

    useManualAdvertiser() {
      if (!this.manualAdvertiserId) {
        return
      }

      this.selectedAdvertiserId = this.manualAdvertiserId
      this.currentPage = 1
      this.fetchTasks()

      this.toastSuccess(`Now using advertiser ID: ${this.manualAdvertiserId}`)
    },

    // Access token methods removed - authentication handled by backend

    async saveAsDefaultAdvertiser() {
      if (!this.manualAdvertiserId) {
        return
      }

      this.savingDefault = true

      try {
        // Save to localStorage for immediate persistence
        const defaultAdvertiserKey = 'defaultPreviewAdvertiserId'
        localStorage.setItem(defaultAdvertiserKey, this.manualAdvertiserId)

        // Also save to user preferences in userData
        const userData = localStorage.getItem('userData')
        if (userData) {
          const parsedData = JSON.parse(userData)
          parsedData.defaultPreviewAdvertiserId = this.manualAdvertiserId
          localStorage.setItem('userData', JSON.stringify(parsedData))
        }

        // Update the selectedAdvertiserId to reflect the new default immediately
        this.selectedAdvertiserId = this.manualAdvertiserId

        // Show success toast
        this.toastSuccess(`Advertiser ${this.manualAdvertiserId} saved as default for task history API`)

        // Optional: You can also send this to backend API for server-side persistence
        // await this.saveUserPreference('defaultPreviewAdvertiserId', this.selectedAdvertiserId)
      } catch (error) {
        this.toastFailure('Failed to save default advertiser. Please try again.')
      } finally {
        this.savingDefault = false
      }
    },

    debugAdvertiserState() {
      this.toastSuccess(`Manual: ${this.manualAdvertiserId}, Selected: ${this.selectedAdvertiserId}, Preview: ${this.previewAdvertiserId}`)
    },

    onFilterChange(newFilters) {
      this.filters = { ...newFilters }
      this.currentPage = 1
      this.fetchTasks()
    },

    clearFilters() {
      this.filters.status = ''
      this.filters.tiktokTaskId = ''
      // Don't clear the advertiser selection when clearing filters
      this.onFilterChange(this.filters)
    },

    onPageChange(page) {
      this.currentPage = page
      this.fetchTasks()
    },
  },
}
</script>

<style lang="scss" scoped>
@import './task-log.scss';
</style>
